import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActions = ({ onEmergencyBooking, onRegularBooking, onContactSupport }) => {
  const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);

  const quickActionItems = [
    {
      id: 'emergency',
      title: 'Emergency Service',
      description: 'Urgent cleaning needed',
      icon: 'AlertTriangle',
      color: 'bg-error text-white',
      hoverColor: 'hover:bg-error/90',
      action: () => setEmergencyModalOpen(true)
    },
    {
      id: 'book',
      title: 'Book Service',
      description: 'Schedule regular cleaning',
      icon: 'Calendar',
      color: 'bg-primary text-white',
      hoverColor: 'hover:bg-primary/90',
      action: onRegularBooking
    },
    {
      id: 'support',
      title: 'Contact Support',
      description: 'Get help & assistance',
      icon: 'MessageCircle',
      color: 'bg-accent text-white',
      hoverColor: 'hover:bg-accent/90',
      action: onContactSupport
    },
    {
      id: 'quote',
      title: 'Get Quote',
      description: 'Estimate service cost',
      icon: 'Calculator',
      color: 'bg-secondary text-white',
      hoverColor: 'hover:bg-secondary/90',
      action: () => console.log('Get quote clicked')
    }
  ];

  const emergencyServices = [
    {
      id: 'flood',
      name: 'Water Damage Cleanup',
      description: 'Immediate water extraction and drying',
      price: 299,
      duration: '2-4 hours',
      icon: 'Droplets'
    },
    {
      id: 'fire',
      name: 'Fire Damage Restoration',
      description: 'Smoke and soot removal',
      price: 399,
      duration: '4-6 hours',
      icon: 'Flame'
    },
    {
      id: 'biohazard',
      name: 'Biohazard Cleanup',
      description: 'Safe biological material removal',
      price: 499,
      duration: '3-5 hours',
      icon: 'Shield'
    },
    {
      id: 'mold',
      name: 'Mold Remediation',
      description: 'Emergency mold removal and treatment',
      price: 349,
      duration: '3-4 hours',
      icon: 'AlertCircle'
    }
  ];

  const handleEmergencyBooking = (serviceId) => {
    onEmergencyBooking(serviceId);
    setEmergencyModalOpen(false);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-elevation-2 border border-border">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">
            Quick Actions
          </h2>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {quickActionItems?.map((item) => (
              <button
                key={item?.id}
                onClick={item?.action}
                className={`${item?.color} ${item?.hoverColor} rounded-lg p-4 text-left transition-all duration-200 hover:shadow-elevation-2 hover:scale-105`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Icon name={item?.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">{item?.title}</h3>
                    <p className="text-sm opacity-90">{item?.description}</p>
                  </div>
                  <Icon name="ArrowRight" size={16} />
                </div>
              </button>
            ))}
          </div>

          {/* Additional Quick Links */}
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-lg font-medium text-text-primary mb-4">
              Quick Links
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="FileText"
                iconPosition="left"
                fullWidth
              >
                Invoices
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Star"
                iconPosition="left"
                fullWidth
              >
                Reviews
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Gift"
                iconPosition="left"
                fullWidth
              >
                Rewards
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                iconPosition="left"
                fullWidth
              >
                Settings
              </Button>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-lg font-medium text-text-primary mb-4">
              Need Help?
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm">
                <Icon name="Phone" size={16} color="var(--color-primary)" />
                <span className="text-text-secondary">24/7 Emergency:</span>
                <a href="tel:+1234567890" className="text-primary font-medium hover:underline">
                  (123) 456-7890
                </a>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <Icon name="MessageSquare" size={16} color="var(--color-primary)" />
                <span className="text-text-secondary">WhatsApp:</span>
                <a href="https://wa.me/1234567890" className="text-primary font-medium hover:underline">
                  Chat Now
                </a>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <Icon name="Mail" size={16} color="var(--color-primary)" />
                <span className="text-text-secondary">Email:</span>
                <a href="mailto:support@clean360.com" className="text-primary font-medium hover:underline">
                  support@clean360.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Emergency Service Modal */}
      {emergencyModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-error rounded-lg flex items-center justify-center">
                    <Icon name="AlertTriangle" size={20} color="white" />
                  </div>
                  <h2 className="text-xl font-semibold text-text-primary">
                    Emergency Services
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEmergencyModalOpen(false)}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Clock" size={16} color="var(--color-error)" />
                  <span className="text-sm font-medium text-error">
                    Emergency Response Available 24/7
                  </span>
                </div>
                <p className="text-sm text-text-secondary">
                  Our emergency team can be at your location within 1-2 hours. Additional surge pricing may apply for after-hours service.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {emergencyServices?.map((service) => (
                  <div
                    key={service?.id}
                    className="border border-border rounded-lg p-4 hover:shadow-elevation-1 transition-shadow duration-200"
                  >
                    <div className="flex items-start space-x-3 mb-3">
                      <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
                        <Icon name={service?.icon} size={20} color="var(--color-error)" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-text-primary">{service?.name}</h3>
                        <p className="text-sm text-text-secondary mt-1">{service?.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm text-text-secondary">
                        Duration: {service?.duration}
                      </div>
                      <div className="text-lg font-semibold text-text-primary">
                        ${service?.price}
                      </div>
                    </div>
                    
                    <Button
                      variant="destructive"
                      fullWidth
                      onClick={() => handleEmergencyBooking(service?.id)}
                      iconName="Phone"
                      iconPosition="left"
                    >
                      Book Emergency Service
                    </Button>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-border text-center">
                <p className="text-sm text-text-secondary mb-4">
                  Can't find what you need? Call our emergency hotline
                </p>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Phone"
                  iconPosition="left"
                  onClick={() => window.location.href = 'tel:+1234567890'}
                >
                  Call Emergency Line
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickActions;